import { Trash2, ChevronUp, ChevronDown, GripVertical, Edit, X, Layers, ImageIcon, Settings2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import type { LandingSection } from '@easyflow/shared';
import { SECTION_TYPE_LABELS } from './types';

function sectionHasImageFields(section: LandingSection): boolean {
  const props = section?.props as Record<string, unknown> | undefined;
  if (!props) return false;
  if (props.backgroundImage) return true;
  if (Array.isArray(props.images) && props.images.length > 0) return true;
  if (
    Array.isArray(props.items) &&
    props.items.some((item: { image?: unknown; url?: unknown }) =>
      item.image || (typeof item.url === 'string' && item.url.match(/\.(jpg|jpeg|png|webp|gif|svg)/i))
    )
  ) return true;
  const imageTypes = ['hero', 'gallery', 'features', 'logos', 'team', 'portfolio'];
  return imageTypes.includes(section.type ?? '');
}

const WIDGET_TYPES = ['chatbot_embed', 'voice_agent_embed', 'gemini_voice_embed'];

interface SectionPanelProps {
  sections: LandingSection[];
  selectedSectionId: string | null;
  onSelectSection: (id: string | null) => void;
  onReorder: (newSections: LandingSection[]) => void;
  onDelete: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onClose: () => void;
  onEditProperties: (sectionId: string) => void;
  onConfigureWidget?: (sectionId: string) => void;
  onReplaceImage?: (sectionId: string) => void;
}

function SortableItem({ section, index, total, isSelected, onSelect, onDelete, onMoveUp, onMoveDown, onEditProperties, onConfigureWidget, onReplaceImage }: {
  section: LandingSection;
  index: number;
  total: number;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onEditProperties: () => void;
  onConfigureWidget?: () => void;
  onReplaceImage?: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id ?? '' });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 0,
  };

  const sectionType = section.type ?? '';
  const props = section.props as Record<string, unknown> | undefined;
  const propsTitle = typeof props?.title === 'string' ? props.title : undefined;
  const typeLabel = SECTION_TYPE_LABELS[sectionType] || sectionType;
  const sectionTitle = propsTitle || typeLabel;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-xs transition-all ${
        isSelected
          ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-300 dark:border-indigo-700'
          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300'
      }`}
    >
      <button
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={onSelect}
        className="flex-1 text-left truncate text-gray-900 dark:text-white font-medium"
        title={sectionTitle}
      >
        <span className="text-[10px] text-gray-400 uppercase mr-1">{typeLabel}</span>
        {propsTitle && <span className="text-gray-600 dark:text-gray-300">· {propsTitle}</span>}
      </button>
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={onEditProperties} className="p-0.5 text-gray-400 hover:text-indigo-600" title="Proprietà">
          <Edit className="h-3 w-3" />
        </button>
        {WIDGET_TYPES.includes(sectionType) && onConfigureWidget && (
          <button onClick={onConfigureWidget} className="p-0.5 text-gray-400 hover:text-amber-500" title="Configura widget">
            <Settings2 className="h-3 w-3" />
          </button>
        )}
        {sectionHasImageFields(section) && onReplaceImage && (
          <button onClick={onReplaceImage} className="p-0.5 text-gray-400 hover:text-sky-600" title="Sostituisci immagine">
            <ImageIcon className="h-3 w-3" />
          </button>
        )}
        <button onClick={onMoveUp} disabled={index === 0} className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30" title="Sposta su">
          <ChevronUp className="h-3 w-3" />
        </button>
        <button onClick={onMoveDown} disabled={index === total - 1} className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30" title="Sposta giù">
          <ChevronDown className="h-3 w-3" />
        </button>
        <button onClick={onDelete} className="p-0.5 text-gray-400 hover:text-red-500" title="Elimina">
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

export default function SectionPanel({
  sections,
  selectedSectionId,
  onSelectSection,
  onReorder,
  onDelete,
  onMoveUp,
  onMoveDown,
  onClose,
  onEditProperties,
  onConfigureWidget,
  onReplaceImage,
}: SectionPanelProps) {
  const safeSections: (LandingSection & { id: string })[] = sections.map((s, i) => ({
    ...s,
    id: s.id ?? `__section_${i}`,
  }));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = safeSections.findIndex(s => s.id === active.id);
    const newIndex = safeSections.findIndex(s => s.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      onReorder(arrayMove(sections, oldIndex, newIndex));
    }
  };

  return (
    <div className="w-[240px] min-w-[240px] border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center gap-1.5">
          <Layers className="h-3.5 w-3.5 text-indigo-500" />
          <span className="text-xs font-semibold text-gray-900 dark:text-white">Sezioni ({sections.length})</span>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {sections.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">Nessuna sezione</p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={safeSections.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {safeSections.map((section, idx) => (
                <SortableItem
                  key={section.id}
                  section={section}
                  index={idx}
                  total={sections.length}
                  isSelected={selectedSectionId === section.id}
                  onSelect={() => onSelectSection(selectedSectionId === section.id ? null : section.id)}
                  onDelete={() => onDelete(idx)}
                  onMoveUp={() => onMoveUp(idx)}
                  onMoveDown={() => onMoveDown(idx)}
                  onEditProperties={() => onEditProperties(section.id)}
                  onConfigureWidget={onConfigureWidget ? () => onConfigureWidget(section.id) : undefined}
                  onReplaceImage={onReplaceImage ? () => onReplaceImage(section.id) : undefined}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}
